<template>
    <Dialog
        :value="showDialog"
        :header-text="headerText"
        :width="920"
        persistent
        hide-close-icon
        fixed
        @close_modal="$emit('close')"
    >
        <div>
            <Options v-if="controller.model.showOptions()" :controller="controller"/>
            <Email
                ref="emailEdit"
                :controller="controller"
                @focusInfo="setFocusedField($event)"
            />
        </div>
        <template slot="footer">
            <Footer
                :type="type"
                :controller="controller"
                :rich-text-editor="richTextEditor"
                @placeholder="addPlaceholder($event)"
                @placeholders="setAutoSignature($event)"
                @sendTestEmail="onSendTestEmail"
            />
        </template>
    </Dialog>
</template>

<script>
import Options from "./Options";
import Email from "./Email";
import Footer from "./Footer";
import { $t } from "@/i18n";
import { computed, ref, watch, reactive } from "@vue/composition-api";
import AccountModule from "../../../store/modules/account";
import AppModule from "../../../store/modules/app";
import RightsModule from "../../../composables/use-rights";
import Dialog from "../../../components/Dialog";
import Controller from "./controller"
import EmailSignatureModule from "../../../api/email_automation/email_signature";

export default {
    name: "EditTemplateIndex",
    components: {
        Options,
        Email,
        Footer,
        Dialog
    },
    props: {
        template: {
            type: Object,
            default: null
        },
        errors: {
            type: [Error, Object],
            required: true
        },
        form_data: {
            type: Object,
            default: null
        },
        showDialog: {
            type: Boolean,
            default: null
        },
        titleText: {
            type: String,
            default: () => $t("Subject")
        },
        loader: {
            type: Object,
            default: null
        },
        companyId: {
            type: Number,
            required: true
        },
        overrideLocaleCode: {
            type: String,
            default: null
        },
        headerText: {
            type: String,
            required: true
        },
        showPlaceholders: {
            type: Boolean,
            required: true
        },
        type: {
            type: String,
            required: true
        },
        channel: {
            type: String,
            required: true
        }
    },
    setup(props, ctx) {
        const { current_account } = AccountModule();
        const { can_edit_account } = RightsModule();
        const emailEdit = ref(null);
        const { languages, fetchClassifiers } = AppModule();
        const { getMemberDefaultSignature } = EmailSignatureModule()
        const focusInfo = ref({fieldName: "content", cursorIndex: null})
        const autoSignatureAdded = ref(false)


        const isNewTemplate = computed(() => {
            return !props.template.id
        })

        const onDelete = () => {
            ctx.emit('delete')
        }

        const onSave = (template) => {
            ctx.emit('save', template)
        }

        const onCancel = () => {
            ctx.emit('cancel')
        }

        const onSendTestEmail = (template) => {
            ctx.emit('sendTestEmail', template)
        }

        const canEditCompany = can_edit_account(props.companyId)

        /** @type {Controller} */
        const controller = reactive(
            new Controller(
                props.showPlaceholders,
                props.companyId,
                onDelete,
                onSave,
                onCancel,
                languages,
                canEditCompany,
                props.loader,
                props.overrideLocaleCode,
                current_account.value.locale_code,
                props.titleText,
                props.type,
                props.channel,
                onSendTestEmail,
            )
        )

        fetchClassifiers({types: ["languages"], account_id: current_account.value.id});

        const richTextEditor = computed(() => {
            if (!emailEdit.value) {
                return
            }

            return emailEdit.value.$refs.richTextEditor
        })

        const addPlaceholder = (placeholder) => {
            if (!focusInfo.value) {
                return
            }
            const fieldName = focusInfo.value?.fieldName
            if (fieldName === "content") {
                let quill = richTextEditor.value.$refs.richTextEditorContent.quill
                let selection = quill.getSelection(true)
                quill.insertText(selection.index, placeholder.value)
            } else if (fieldName === "subject") {
                const cursorIndex = focusInfo.value.cursorIndex
                const subjectValue = controller.model.getSubject()
                const firstPart = subjectValue.slice(0, cursorIndex);
                const secondPart = subjectValue.slice(cursorIndex);
                const newSubjectValue = firstPart + placeholder.value + secondPart
                const newCursorIndex = focusInfo.value.cursorIndex + placeholder.value.length
                focusInfo.value.cursorIndex = newCursorIndex
                controller.setSubject(newSubjectValue)

                // Set focus back to the subject field
                const inputElement = emailEdit.value.$refs.subjectTextField.$refs.textField.$refs.input
                inputElement.focus()
                setTimeout(() => {
                    inputElement.setSelectionRange(newCursorIndex, newCursorIndex);
                }, 0);
            }
        }

        const setFocusedField = (event) => {
            focusInfo.value = event
        }

        const setAutoSignature = (placeholders) => {
            if (!isNewTemplate.value || autoSignatureAdded.value) {
                return
            }

            const signature_member_default = getMemberDefaultSignature(placeholders)
            if (signature_member_default && signature_member_default.value) {
                // set
                let quill = richTextEditor.value.$refs.richTextEditorContent.quill
                insertNewLinesToQuill(quill)
                quill.insertText(3, signature_member_default.value);
                autoSignatureAdded.value = true
            }
        }

        const insertNewLinesToQuill = (quill) => {
            const editorContent = quill.getText();

            const lines = editorContent.split('\n');
            const linesToAdd = 3 - lines.length

            for (let i = 0; i < linesToAdd; i++) {
                quill.insertText(0, '\n', 'user')
            }
        }

        watch(() => props.showDialog, () => {
            if (props.showDialog) {
                controller.loadTemplate(props.template)
            }
        })

        watch(() => props.errors, () => {
            if (
                props.errors &&
                props.errors.response &&
                props.errors.response.data.error_type &&
                props.errors.response.data.error_type === "form_validation"
            ) {
                controller.showValidationErrors(props.errors.response.data.errors)
            }
        })

        return {
            emailEdit,
            richTextEditor,
            controller,
            addPlaceholder,
            setFocusedField,
            setAutoSignature,
            onSendTestEmail,
        }
    }
}
</script>

<style scoped>

</style>